import { Info } from '@crehana/compass.icons.outline/v2/info';
import { Button } from '@crehana/compass.ui/v3/button';
import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerPopup,
  DrawerPortal,
  DrawerTitle,
  DrawerViewport,
} from '@crehana/compass.ui/v3/drawer';
import { EmptyState } from '@crehana/compass.ui/v3/empty-state';
import { Tag } from '@crehana/compass.ui/v3/tag';
import { BasicTooltip } from '@crehana/compass.ui/v3/tooltip';
import { format } from '@crehana/date/format';
import { DATE_FORMATS } from '@crehana/date/presets';
import { parseISO } from 'date-fns';

import { TimeOvertimeRequestStatus } from '@/generated/graphql/types';

import { STATUS_CONFIG } from '../../constants';
import { useAddRequestComment } from '../../hooks/use-add-request-comment';
import { useRequestDetail } from '../../hooks/use-request-detail';
import { useTranslation } from '../../i18n';
import { formatMinutes, formatShiftName } from '../../utils';
import { CommentsThread } from './components/comments-thread';
import { DetailRow } from './components/detail-row';
import { RequestDetailSkeleton } from './components/request-detail-skeleton';
import { RequesterBanner } from './components/requester-banner';
import { RespondedByRow } from './components/responded-by-row';

type TRequestDetailDrawerProps = {
  requestId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenChangeComplete?: (open: boolean) => void;
};

export const RequestDetailDrawer = ({
  requestId,
  open,
  onOpenChange,
  onOpenChangeComplete,
}: TRequestDetailDrawerProps) => {
  const { t, languageCode } = useTranslation();
  const { detail, kind, refetch } = useRequestDetail(requestId);
  const { addComment, submitting } = useAddRequestComment(requestId);

  // The schema says `responded_by` is null for every Pending request. The
  // status check only guards against a backend change.
  const showRespondedBy =
    detail?.responded_by != null &&
    detail.status !== TimeOvertimeRequestStatus.Pending;

  const title = detail
    ? t('REQUEST_DETAIL_DRAWER_TITLE', {
        interpolation: {
          date: format({
            date: parseISO(detail.request_date),
            pattern: "d 'de' MMMM",
            languageCode,
          }),
        },
      })
    : t('REQUEST_DETAIL_DRAWER_TITLE_FALLBACK');

  // The final-amount rows and the icon show only when the backend flags a
  // difference. The excess itself is never shown (AC-7).
  const showFinalAmounts = detail?.has_final_amount_difference === true;
  const finalAmountAdornment = showFinalAmounts ? (
    <BasicTooltip content={t('STATUS_UPDATED_TOOLTIP')}>
      <span
        data-testid="final-amount-difference-icon"
        className="tw-inline-flex tw-cds-text-secondary"
      >
        <Info size={16} />
      </span>
    </BasicTooltip>
  ) : undefined;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      size="md"
    >
      <DrawerPortal>
        <DrawerBackdrop />
        <DrawerViewport>
          <DrawerPopup>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>{title}</DrawerTitle>
                <DrawerCloseButton />
              </DrawerHeader>

              {kind === 'ready' && detail?.user ? (
                <RequesterBanner user={detail.user} />
              ) : null}
              <DrawerBody className="tw-flex tw-flex-col tw-cds-gap-6">
                {kind === 'loading' ? <RequestDetailSkeleton /> : null}

                {kind === 'forbidden' ? (
                  <EmptyState variant="no-access" language={languageCode}>
                    <EmptyState.Title>
                      {t('REQUEST_DETAIL_FORBIDDEN_TITLE')}
                    </EmptyState.Title>
                    <EmptyState.Description>
                      {t('REQUEST_DETAIL_FORBIDDEN_DESCRIPTION')}
                    </EmptyState.Description>
                  </EmptyState>
                ) : null}

                {kind === 'error' ? (
                  <EmptyState variant="error" language={languageCode}>
                    <EmptyState.Title>
                      {t('TABLE_ERROR_TITLE')}
                    </EmptyState.Title>
                    <EmptyState.Description>
                      {t('TABLE_ERROR_DESCRIPTION')}
                    </EmptyState.Description>
                    <EmptyState.Actions>
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        onClick={refetch}
                      >
                        {t('ERROR_STATE_RETRY')}
                      </Button>
                    </EmptyState.Actions>
                  </EmptyState>
                ) : null}

                {kind === 'ready' && detail ? (
                  <>
                    <div className="tw-grid tw-grid-cols-2 tw-cds-gap-5">
                      <DetailRow
                        label={t('TABLE_HEADER_STATUS')}
                        value={
                          <Tag variant={STATUS_CONFIG[detail.status].variant}>
                            {t(STATUS_CONFIG[detail.status].tKey)}
                          </Tag>
                        }
                      />
                      <DetailRow
                        label={t('TABLE_HEADER_SHIFT')}
                        value={formatShiftName(detail.shift_name)}
                      />
                      <DetailRow
                        label={t('REQUEST_DETAIL_CREATED_AT_LABEL')}
                        value={format({
                          date: parseISO(detail.created_at),
                          pattern: DATE_FORMATS.SHORT,
                          languageCode,
                        })}
                      />
                      <DetailRow
                        label={t('REQUEST_DETAIL_OVERTIME_REQUESTED_LABEL')}
                        value={formatMinutes(detail.extra_minutes)}
                      />
                      <DetailRow
                        label={t('REQUEST_DETAIL_COMPENSATION_REQUESTED_LABEL')}
                        value={formatMinutes(detail.replacement_minutes)}
                      />
                      {showFinalAmounts &&
                      detail.final_extra_minutes != null ? (
                        <DetailRow
                          label={t('REQUEST_DETAIL_OVERTIME_FINAL_LABEL')}
                          value={formatMinutes(detail.final_extra_minutes)}
                          adornment={finalAmountAdornment}
                        />
                      ) : null}
                      {showFinalAmounts &&
                      detail.final_replacement_minutes != null ? (
                        <DetailRow
                          label={t('REQUEST_DETAIL_COMPENSATION_FINAL_LABEL')}
                          value={formatMinutes(
                            detail.final_replacement_minutes,
                          )}
                          adornment={finalAmountAdornment}
                        />
                      ) : null}
                    </div>

                    <DetailRow
                      label={t('REQUEST_DETAIL_REASON_LABEL')}
                      value={detail.reason}
                    />

                    {showRespondedBy && detail.responded_by ? (
                      <RespondedByRow responder={detail.responded_by} />
                    ) : null}

                    <CommentsThread
                      comments={detail.comments}
                      submitting={submitting}
                      onSubmit={addComment}
                    />
                  </>
                ) : null}
              </DrawerBody>
            </DrawerContent>
          </DrawerPopup>
        </DrawerViewport>
      </DrawerPortal>
    </Drawer>
  );
};
